/*
 * Ported from the IDV proof of concept (frontend/src/utils/documentCameraSelection.ts).
 *
 * Mechanical TypeScript -> JavaScript conversion (tsc --target ES2020, types
 * erased). The algorithms, thresholds and geometry are unchanged: this file is
 * pinned by src/utils/documentCrop.test.js and by the IDV backend contract.
 */

import { applyBestDocumentCameraConstraints, getTrackCapabilities, getTrackSettings, stopMediaStream, } from './cameraControls';
import { estimateDocumentDistanceFromPreview, scoreVideoFrame, } from './cameraQuality';
const CACHE_KEY = 'idv_best_document_camera_v1';
const CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 30;
const VIDEO_READY_TIMEOUT_MS = 1400;
const WARMUP_MS = 250;
const SAMPLE_COUNT = 2;
const SAMPLE_DELAY_MS = 80;
const STREAM_SETTLE_MS = 150;
const BLACK_FRAME_BRIGHTNESS = 5;
const BLACK_FRAME_CONTRAST = 2;
const STRONG_INITIAL_SCORE = 1250;
// Manual camera QA: enable ?debug=1 or localStorage idv_camera_debug=1,
// then verify Samsung prefers the sharp rear camera, iPhone rejects front/black
// document candidates, front/back/passport reuse cache, and selfie stays user-facing.
export function classifyCameraLabel(labelValue) {
    const label = normalizeLabel(labelValue);
    if (!label)
        return 'unknown';
    if (includesAny(label, [
        'front',
        'user',
        'selfie',
        'anteriore',
        'frontale',
        'frontal',
        'avant',
        'frente',
    ])) {
        return 'front';
    }
    if (includesAny(label, [
        'back',
        'rear',
        'environment',
        'facing back',
        'posteriore',
        'posterior',
        'dorsal',
        'triple',
        'tripla',
        'wide',
    ])) {
        return 'rear';
    }
    return 'unknown';
}
export async function selectBestDocumentCamera(options) {
    const warnings = [];
    let permissionStream = null;
    let videoInputs = await enumerateVideoInputDevices();
    const labelsAvailable = videoInputs.some((device) => device.label.trim());
    if (!labelsAvailable) {
        try {
            permissionStream = await requestCameraStream({
                ...options.constraints,
                facingMode: { exact: 'environment' },
            });
        }
        catch (error) {
            warnings.push(errorMessage('strict_environment_permission_failed', error));
            try {
                permissionStream = await requestCameraStream(options.constraints);
            }
            catch (fallbackError) {
                warnings.push(errorMessage('environment_permission_failed', fallbackError));
            }
        }
        finally {
            await stopMediaStream(permissionStream);
        }
        videoInputs = await enumerateVideoInputDevices();
    }
    const deviceSignature = buildDeviceSignature(videoInputs);
    const userAgentHash = hashString(navigator.userAgent || 'unknown');
    const results = [];
    const cached = readCachedCamera();
    const cachedDevice = cached && cacheMatches(cached, videoInputs, deviceSignature, userAgentHash)
        ? videoInputs.find((device) => device.deviceId === cached.deviceId)
        : null;
    if (cached && !cachedDevice) {
        warnings.push('cached_document_camera_invalidated');
    }
    if (cached && cachedDevice) {
        const cachedProbe = await probeDocumentCamera(cachedDevice, {
            ...options,
            keepStream: true,
        });
        results.push(withoutStream(cachedProbe));
        if (cachedProbe.valid && cachedProbe.stream) {
            return {
                selectedDeviceId: cachedProbe.deviceId,
                selectedLabel: cachedProbe.label,
                selectedStream: cachedProbe.stream,
                selectedDeviceIdFromCache: cached.deviceId,
                selectedLabelFromCache: cached.label,
                selection_source: 'cache',
                source: 'cache',
                results,
                warnings,
                videoInputs,
            };
        }
        warnings.push(`cached_document_camera_rejected:${cachedProbe.rejectedReason}`);
        await stopMediaStream(cachedProbe.stream);
    }
    const candidates = orderedProbeCandidates(videoInputs, options.previousDeviceId, cachedDevice?.deviceId ?? null);
    for (const device of candidates) {
        if (classifyCameraLabel(device.label) === 'front') {
            results.push(skippedProbeResult(device, 'front_label_for_document'));
            continue;
        }
        const probe = await probeDocumentCamera(device, options);
        results.push(withoutStream(probe));
        await stopMediaStream(probe.stream);
        if (shouldStopAfterStrongProbe(probe)) {
            warnings.push('document_camera_probe_stopped_after_strong_candidate');
            break;
        }
    }
    const selectedResult = [...results]
        .filter((result) => result.valid)
        .sort((left, right) => right.score - left.score)[0];
    if (selectedResult?.deviceId) {
        const selectedDevice = videoInputs.find((device) => device.deviceId === selectedResult.deviceId);
        const selectedStream = selectedDevice
            ? await requestDeviceStream(selectedDevice.deviceId, options.constraints, options.fallbackConstraints).catch((error) => {
                warnings.push(errorMessage('selected_document_camera_open_failed', error));
                return null;
            })
            : null;
        if (selectedStream) {
            writeCachedCamera({
                deviceId: selectedResult.deviceId,
                label: selectedResult.label,
                deviceSignature,
                userAgentHash,
                timestamp: Date.now(),
                lastScore: selectedResult.score,
            });
            return {
                selectedDeviceId: selectedResult.deviceId,
                selectedLabel: selectedResult.label,
                selectedStream,
                selectedDeviceIdFromCache: cached?.deviceId ?? null,
                selectedLabelFromCache: cached?.label ?? null,
                selection_source: 'probe',
                source: 'probe',
                results,
                warnings,
                videoInputs,
            };
        }
    }
    const fallback = await requestFacingModeFallback(options.constraints, options.fallbackConstraints, warnings);
    if (fallback.stream) {
        return {
            selectedDeviceId: fallback.deviceId,
            selectedLabel: fallback.label,
            selectedStream: fallback.stream,
            selectedDeviceIdFromCache: cached?.deviceId ?? null,
            selectedLabelFromCache: cached?.label ?? null,
            selection_source: fallback.source,
            source: fallback.source,
            results,
            warnings,
            videoInputs,
        };
    }
    return {
        selectedDeviceId: null,
        selectedLabel: null,
        selectedStream: null,
        selectedDeviceIdFromCache: cached?.deviceId ?? null,
        selectedLabelFromCache: cached?.label ?? null,
        selection_source: 'generic-fallback',
        source: 'generic-fallback',
        results,
        warnings: [...warnings, 'document_camera_selection_failed'],
        videoInputs,
    };
}
export async function openDocumentCameraByDeviceId(options) {
    return requestDeviceStream(options.deviceId, options.constraints, options.fallbackConstraints);
}
async function probeDocumentCamera(device, options) {
    const classification = classifyCameraLabel(device.label);
    const warnings = [];
    let stream = null;
    let video = null;
    const baseResult = () => ({
        deviceId: device.deviceId,
        label: device.label || null,
        classification,
        started: false,
        valid: false,
        rejectedReason: null,
        facingMode: null,
        width: null,
        height: null,
        zoom: null,
        muted: null,
        readyState: null,
        blackFrame: false,
        sharpness: null,
        brightness: null,
        contrast: null,
        nativePhotoSupported: null,
        nativePhotoWidth: null,
        nativePhotoHeight: null,
        capabilityWidthMax: null,
        capabilityHeightMax: null,
        score: -9999,
        warnings,
        stream: null,
    });
    try {
        stream = await requestDeviceStream(device.deviceId, options.constraints, options.fallbackConstraints);
        await wait(STREAM_SETTLE_MS);
        const track = stream.getVideoTracks()[0] ?? null;
        const result = baseResult();
        result.started = true;
        result.stream = options.keepStream ? stream : null;
        if (!track) {
            return rejectProbe(result, 'video_track_unavailable', stream);
        }
        await applyBestDocumentCameraConstraints(track);
        video = createHiddenPreviewVideo(stream);
        const videoReady = await waitForVideoReady(video);
        await wait(WARMUP_MS);
        const settings = getTrackSettings(track);
        const capabilities = getTrackCapabilities(track);
        const scores = await sampleVideoScores(video);
        const averagedScore = averageScores(scores);
        const distanceEstimate = estimateDocumentDistanceFromPreview(video);
        const muted = track.muted;
        const readyState = track.readyState;
        const facingMode = settings.facingMode ?? null;
        const width = (settings.width ?? video.videoWidth) || null;
        const height = (settings.height ?? video.videoHeight) || null;
        const blackFrame = isBlackFrame(averagedScore);
        result.facingMode = facingMode;
        result.width = width;
        result.height = height;
        result.zoom = settings.zoom ?? null;
        result.muted = muted;
        result.readyState = readyState;
        result.blackFrame = blackFrame;
        result.sharpness = averagedScore?.sharpness ?? null;
        result.brightness = averagedScore?.brightness ?? null;
        result.contrast = averagedScore?.contrast ?? null;
        result.nativePhotoSupported = null;
        result.nativePhotoWidth = null;
        result.nativePhotoHeight = null;
        result.capabilityWidthMax = capabilityMax(capabilities.width);
        result.capabilityHeightMax = capabilityMax(capabilities.height);
        if (!videoReady)
            return rejectProbe(result, 'video_preview_not_ready', stream);
        if (readyState !== 'live')
            return rejectProbe(result, 'track_not_live', stream);
        if (muted)
            return rejectProbe(result, 'track_muted', stream);
        if (!width || !height)
            return rejectProbe(result, 'invalid_video_dimensions', stream);
        if (facingMode === 'user' || classification === 'front') {
            return rejectProbe(result, 'front_camera_for_document', stream);
        }
        if (!averagedScore)
            return rejectProbe(result, 'score_unavailable', stream);
        if (blackFrame)
            return rejectProbe(result, 'black_frame_detected', stream);
        result.score = scoreProbeResult(result, distanceEstimate);
        result.valid = result.score > -1000;
        result.rejectedReason = result.valid ? null : 'low_camera_score';
        if (!result.valid) {
            return rejectProbe(result, result.rejectedReason, stream);
        }
        if (video)
            disposeHiddenPreviewVideo(video);
        if (!options.keepStream) {
            result.stream = null;
            await stopMediaStream(stream);
        }
        return result;
    }
    catch (error) {
        const result = baseResult();
        result.rejectedReason = 'camera_probe_failed';
        result.warnings.push(errorMessage('camera_probe_failed', error));
        await stopMediaStream(stream);
        return result;
    }
    finally {
        if (video)
            disposeHiddenPreviewVideo(video);
    }
}
function scoreProbeResult(result, distanceEstimate) {
    const label = `${result.label ?? ''} ${result.deviceId}`.toLowerCase();
    const resolutionScore = result.width && result.height
        ? clamp((result.width * result.height) / 6500, 0, 420)
        : 0;
    const capabilityScore = result.capabilityWidthMax && result.capabilityHeightMax
        ? clamp((result.capabilityWidthMax * result.capabilityHeightMax) / 14000, 0, 260)
        : 0;
    const nativePhotoScore = result.nativePhotoWidth && result.nativePhotoHeight
        ? clamp((result.nativePhotoWidth * result.nativePhotoHeight) / 32000, 0, 320)
        : 0;
    const sharpnessScore = clamp((result.sharpness ?? 0) / 4, 0, 520);
    const contrastScore = clamp((result.contrast ?? 0) * 7, 0, 260);
    const facingScore = result.facingMode === 'environment' ? 700 : 0;
    const classificationScore = result.classification === 'rear' ? 220 : 0;
    const zoomPenalty = typeof result.zoom === 'number' && result.zoom > 1.05 ? 140 : 0;
    const distancePenalty = distanceEstimate?.tooClose ? 80 : 0;
    const labelPenalty = cameraRolePenalty(label);
    const mainCameraScore = mainCameraTieBreakScore(label);
    return Math.round(facingScore +
        classificationScore +
        resolutionScore +
        capabilityScore +
        nativePhotoScore +
        sharpnessScore +
        contrastScore +
        mainCameraScore -
        zoomPenalty -
        distancePenalty -
        labelPenalty);
}
function orderedProbeCandidates(devices, previousDeviceId, cachedDeviceId) {
    return devices
        .filter((device) => device.kind === 'videoinput' && device.deviceId)
        .filter((device) => device.deviceId !== cachedDeviceId)
        .map((device, index) => {
        const classification = classifyCameraLabel(device.label);
        const label = `${device.label} ${device.deviceId}`.toLowerCase();
        let priority = 0;
        if (device.deviceId === previousDeviceId)
            priority += 800;
        if (classification === 'rear')
            priority += 500;
        if (classification === 'unknown')
            priority += 120;
        if (classification === 'front')
            priority -= 900;
        priority += mainCameraTieBreakScore(label);
        priority -= cameraRolePenalty(label);
        return { device, index, priority };
    })
        .sort((left, right) => right.priority - left.priority || left.index - right.index)
        .map((item) => item.device);
}
function shouldStopAfterStrongProbe(result) {
    const label = `${result.label ?? ''} ${result.deviceId}`.toLowerCase();
    return (result.valid &&
        result.classification === 'rear' &&
        result.blackFrame === false &&
        result.score >= STRONG_INITIAL_SCORE &&
        cameraRolePenalty(label) === 0);
}
function skippedProbeResult(device, rejectedReason) {
    return {
        deviceId: device.deviceId,
        label: device.label || null,
        classification: classifyCameraLabel(device.label),
        started: false,
        valid: false,
        rejectedReason,
        facingMode: null,
        width: null,
        height: null,
        zoom: null,
        muted: null,
        readyState: null,
        blackFrame: false,
        sharpness: null,
        brightness: null,
        contrast: null,
        nativePhotoSupported: null,
        nativePhotoWidth: null,
        nativePhotoHeight: null,
        capabilityWidthMax: null,
        capabilityHeightMax: null,
        score: -9999,
        warnings: [],
    };
}
async function requestFacingModeFallback(constraints, fallbackConstraints, warnings) {
    try {
        const stream = await requestCameraStream({
            ...constraints,
            facingMode: { exact: 'environment' },
        });
        const track = stream.getVideoTracks()[0] ?? null;
        const settings = track ? getTrackSettings(track) : null;
        return {
            stream,
            deviceId: settings?.deviceId ?? null,
            label: track?.label ?? null,
            source: 'facing-mode-fallback',
        };
    }
    catch (error) {
        warnings.push(errorMessage('strict_facing_mode_fallback_failed', error));
    }
    try {
        const stream = await requestCameraStream(fallbackConstraints);
        const track = stream.getVideoTracks()[0] ?? null;
        const settings = track ? getTrackSettings(track) : null;
        return {
            stream,
            deviceId: settings?.deviceId ?? null,
            label: track?.label ?? null,
            source: 'generic-fallback',
        };
    }
    catch (error) {
        warnings.push(errorMessage('generic_document_camera_fallback_failed', error));
    }
    return {
        stream: null,
        deviceId: null,
        label: null,
        source: 'generic-fallback',
    };
}
async function requestDeviceStream(deviceId, constraints, fallbackConstraints) {
    try {
        return await requestCameraStream(withExactDevice(constraints, deviceId));
    }
    catch {
        return requestCameraStream(withExactDevice(fallbackConstraints, deviceId));
    }
}
async function requestCameraStream(video) {
    return navigator.mediaDevices.getUserMedia({
        video,
        audio: false,
    });
}
function withExactDevice(constraints, deviceId) {
    return {
        ...constraints,
        deviceId: { exact: deviceId },
    };
}
async function enumerateVideoInputDevices() {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter((device) => device.kind === 'videoinput');
    }
    catch {
        return [];
    }
}
function createHiddenPreviewVideo(stream) {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.srcObject = stream;
    void video.play().catch(() => undefined);
    return video;
}
function disposeHiddenPreviewVideo(video) {
    video.pause();
    video.srcObject = null;
    video.remove();
}
async function waitForVideoReady(video) {
    const started = Date.now();
    while (Date.now() - started < VIDEO_READY_TIMEOUT_MS) {
        if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
            return true;
        }
        await wait(80);
    }
    return false;
}
async function sampleVideoScores(video) {
    const scores = [];
    for (let index = 0; index < SAMPLE_COUNT; index += 1) {
        const score = scoreVideoFrame(video, { maxWidth: 420 });
        if (score)
            scores.push(score);
        await wait(SAMPLE_DELAY_MS);
    }
    return scores;
}
function averageScores(scores) {
    if (scores.length === 0)
        return null;
    const total = scores.reduce((acc, score) => ({
        sharpness: acc.sharpness + score.sharpness,
        brightness: acc.brightness + score.brightness,
        glareRatio: acc.glareRatio + score.glareRatio,
        contrast: acc.contrast + score.contrast,
        warnings: [...acc.warnings, ...score.warnings],
    }), {
        sharpness: 0,
        brightness: 0,
        glareRatio: 0,
        contrast: 0,
        warnings: [],
    });
    return {
        sharpness: roundMetric(total.sharpness / scores.length),
        brightness: roundMetric(total.brightness / scores.length),
        glareRatio: roundMetric(total.glareRatio / scores.length),
        contrast: roundMetric(total.contrast / scores.length),
        passed: scores.every((score) => score.passed),
        warnings: [...new Set(total.warnings)],
    };
}
function rejectProbe(result, reason, stream) {
    result.valid = false;
    result.rejectedReason = reason;
    result.score = -9999;
    result.stream = stream;
    return result;
}
function withoutStream(result) {
    const publicResult = { ...result };
    delete publicResult.stream;
    return publicResult;
}
function isBlackFrame(score) {
    if (!score)
        return true;
    return (score.brightness <= BLACK_FRAME_BRIGHTNESS ||
        (score.brightness <= BLACK_FRAME_BRIGHTNESS * 1.8 &&
            score.contrast <= BLACK_FRAME_CONTRAST));
}
function capabilityMax(value) {
    if (!value || typeof value !== 'object' || !('max' in value))
        return null;
    const max = value.max;
    return typeof max === 'number' && Number.isFinite(max) ? max : null;
}
function cameraRolePenalty(label) {
    let penalty = 0;
    if (includesAny(label, [
        'ultra wide',
        'ultrawide',
        'ultra-grandangolare',
        'ultragrandangolare',
    ])) {
        penalty += 220;
    }
    if (includesAny(label, ['macro', 'depth', 'tof']))
        penalty += 260;
    if (includesAny(label, ['telephoto', 'teleobiettivo']))
        penalty += 150;
    if (includesAny(label, ['triple', 'tripla']))
        penalty += 80;
    return penalty;
}
function mainCameraTieBreakScore(label) {
    if (/\bcamera\s*0\b/.test(label) || /\bcamera0\b/.test(label))
        return 90;
    if (/\bmain\b/.test(label) || /\bprincipale\b/.test(label))
        return 80;
    if (/\bwide\b/.test(label) && !label.includes('ultra'))
        return 35;
    if (/\bcamera\s*1\b/.test(label) || /\bcamera1\b/.test(label))
        return 20;
    return 0;
}
function buildDeviceSignature(devices) {
    return devices
        .filter((device) => device.kind === 'videoinput')
        .map((device) => `${device.deviceId}:${device.groupId}:${device.label}`)
        .sort()
        .join('|');
}
function readCachedCamera() {
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (!parsed.deviceId ||
            !parsed.deviceSignature ||
            !parsed.userAgentHash ||
            !parsed.timestamp ||
            Date.now() - parsed.timestamp > CACHE_TTL_MS) {
            return null;
        }
        return {
            deviceId: parsed.deviceId,
            label: parsed.label ?? null,
            deviceSignature: parsed.deviceSignature,
            userAgentHash: parsed.userAgentHash,
            timestamp: parsed.timestamp,
            lastScore: parsed.lastScore ?? 0,
        };
    }
    catch {
        return null;
    }
}
function writeCachedCamera(entry) {
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    }
    catch {
        // Camera cache is opportunistic; capture must not depend on localStorage.
    }
}
function cacheMatches(cached, devices, deviceSignature, userAgentHash) {
    return (cached.deviceSignature === deviceSignature &&
        cached.userAgentHash === userAgentHash &&
        devices.some((device) => device.deviceId === cached.deviceId));
}
function normalizeLabel(label) {
    return label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}
function includesAny(value, needles) {
    return needles.some((needle) => value.includes(needle));
}
function hashString(value) {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(index);
        hash |= 0;
    }
    return String(hash);
}
function errorMessage(prefix, error) {
    return error instanceof Error ? `${prefix}:${error.message}` : prefix;
}
function wait(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function roundMetric(value) {
    return Math.round(value * 1000) / 1000;
}
